import React, { useEffect, useId, useRef, useState } from 'react';
import { formatPackData } from './esimPlanFormat';
import { celebratedKey, type EsimOrderView } from './esimOrderState';
import { EsimInstallPanel } from './EsimInstallPanel';
import { CompatibilityDialog, type CompatTab } from './CompatibilityDialog';
import { trackEsimEvent } from './esimAnalytics';
import './CanadaEsimPage.css';
import './EsimSuccess.css';

export type SuccessKind = 'waiting' | 'preparing' | 'delayed' | 'ready' | 'sorting';

interface Props {
  kind: SuccessKind;
  order: EsimOrderView;
  message?: string;
  onBuyAnother: () => void;
}

const TITLES: Record<SuccessKind, string> = {
  waiting: 'Waiting for your payment',
  preparing: 'Congratulations. Your payment was successful.',
  delayed: 'Congratulations. Your payment was successful.',
  ready: 'Your eSIM is ready',
  sorting: "We're sorting this out",
};

function inr(value?: number): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
}

export const EsimSuccess: React.FC<Props> = ({ kind, order, message, onBuyAnother }) => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const [saved, setSaved] = useState(false);
  const [live, setLive] = useState('');
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [compatOpen, setCompatOpen] = useState(false);
  const [compatTab, setCompatTab] = useState<CompatTab>('check');
  const headingId = useId();
  const data = order.name || order.dataGb ? formatPackData(order) : null;
  const dataLabel = data?.value ? `${data.value} ${data.unit}` : '';
  const price = inr(order.priceInr);
  const rows = [
    order.name ? ['Plan', order.name] : null,
    dataLabel ? ['Data', dataLabel] : null,
    order.validityDays ? ['Validity', `${order.validityDays} days`] : null,
    order.network ? ['Network', order.network] : null,
    order.orderRef ? ['Order ref', order.orderRef] : null,
    order.emailMasked ? ['Also emailed to', order.emailMasked] : null,
  ].filter((row): row is [string, string] => Boolean(row));

  useEffect(() => {
    if (kind !== 'ready' || !order.saleId) return;
    const key = celebratedKey(order.saleId);
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const store = typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function' ? localStorage : null;
    const seen = store?.getItem(key) === '1';
    if (!seen && !reduce) {
      if (store) store.setItem(key, '1');
      const box = badgeRef.current?.getBoundingClientRect();
      const x = box ? box.left + box.width / 2 : window.innerWidth / 2;
      const y = box ? box.top + box.height / 2 : 80;
      const accent = getComputedStyle(document.documentElement).getPropertyValue('--esim-accent').trim() || '#C0322B';
      const ok = getComputedStyle(document.documentElement).getPropertyValue('--esim-ok').trim() || '#17803D';
      const text = getComputedStyle(document.documentElement).getPropertyValue('--esim-text').trim() || '#111114';
      void import('./esimConfetti').then(({ burstConfetti }) => {
        window.setTimeout(() => burstConfetti(x, y, [accent, '#F59E0B', ok, '#6366F1', text]), 500);
      });
    } else if (!seen && store) {
      store.setItem(key, '1');
    }
    setLive('Payment received. Your eSIM is ready to install.');
    const active = document.activeElement;
    if (!active || active === document.body) titleRef.current?.focus();
  }, [kind, order.saleId]);

  const buyAnother = () => {
    if (kind === 'ready' && !saved) {
      setLeaveOpen(true);
      return;
    }
    onBuyAnother();
  };

  const state = kind === 'sorting' ? 'error' : kind === 'delayed' ? 'pending' : kind === 'waiting' ? 'pending' : kind;
  const paid = kind !== 'waiting';

  return (
    <main className="cwy-esim-theme canada-esim esim-success" data-state={state}>
      <div className="esim-live" role="status" aria-live="polite">{live}</div>
      <div className="esim-success-wrap">
        <header className="esim-hero">
          <div className="esim-badge" ref={badgeRef} aria-hidden="true">
            <svg viewBox="0 0 56 56"><circle className="ring" cx="28" cy="28" r="25" /><path className="tick" d="M17 29l8 8 14-16" /></svg>
            <span className="ripple" />
            <span className="ripple ripple-late" />
          </div>
          <div>
            <p className="esim-eyebrow">{kind === 'waiting' ? 'Checkout' : 'Order complete'}</p>
            <h1 id={headingId} ref={titleRef} tabIndex={-1}>{TITLES[kind]}</h1>
            <p className="esim-lede">
              {kind === 'ready' && price && order.name ? `${price} paid for ${order.name}. Install it before you fly.` : null}
              {kind === 'preparing' || kind === 'delayed' ? 'We are getting your eSIM ready. Please wait a moment. You can leave this page. The QR will be here when it is ready.' : null}
              {kind === 'waiting' ? 'Finish the Razorpay window to pay for this pack.' : null}
              {kind === 'sorting' ? (message || "We're sorting this out") : null}
            </p>
          </div>
        </header>
        <div className="esim-grid">
          <section className="esim-card" aria-labelledby="install-title">
            <div className="esim-card-head">
              <h2 id="install-title">Install your eSIM</h2>
            </div>
            {kind === 'sorting' || kind === 'waiting' ? (
              <p className="esim-lede">{kind === 'waiting' ? 'The QR appears after the payment is confirmed.' : 'No QR is available for this order yet.'}</p>
            ) : (
              <EsimInstallPanel
                activationCode={order.activationCode}
                planName={order.name}
                ready={kind === 'ready'}
                onSaved={() => setSaved(true)}
                onLive={setLive}
              >
                <div className="esim-choices">
                  <div className="esim-choice esim-choice-rec">
                    <p className="esim-choice-label">Recommended</p>
                    <p>Scan this QR on the phone that will use the data.</p>
                    <ol className="esim-steps">
                      <li><div>Open Settings and search for eSIM.</div></li>
                      <li><div>Tap Add eSIM.</div></li>
                      <li><div>Choose the option to scan a QR code, then scan this code.</div></li>
                    </ol>
                    <p className="esim-choice-device"><b>iPhone.</b> Settings, Cellular, Add eSIM, Use QR Code.</p>
                    <p className="esim-choice-device"><b>Android.</b> Settings, Network and internet, SIMs, Add eSIM, Scan QR code. On Samsung: Settings, Connections, SIM manager, Add eSIM.</p>
                  </div>
                </div>
              </EsimInstallPanel>
            )}
          </section>
          <div className="esim-stack">
            <div className="esim-buy-row">
              <button type="button" className="esim-btn esim-btn-ghost" onClick={buyAnother}><PlusIcon />Buy another</button>
            </div>
            <section className="esim-card" aria-labelledby="summary-title">
              <div className="esim-card-head">
                <h2 id="summary-title">Order summary</h2>
                {paid ? <span className="esim-pill esim-pill-ok"><CheckIcon />Paid</span> : null}
              </div>
              <dl className="esim-sum">
                {rows.map(([label, value]) => (
                  <div className="esim-row" key={label}><dt>{label}</dt><dd className={label === 'Order ref' ? 'esim-mono' : undefined}>{value}</dd></div>
                ))}
                {price ? <div className="esim-row esim-total"><dt>Total paid</dt><dd>{price}</dd></div> : null}
              </dl>
            </section>
            <section className="esim-card">
              <h2>How to use this eSIM</h2>
              <ol className="esim-steps">
                <li><div><b>Finish on Wi-Fi</b>Let the phone add the eSIM. You can name the line Canada.</div></li>
                <li><div><b>Leave it off until you land</b>Keep your usual SIM on for calls and texts.</div></li>
                <li><div><b>After you land, turn this line on</b>Turn on mobile data for the Canada line.</div></li>
                <li><div><b>Turn on data roaming for this line only</b>Leave roaming off on your home SIM.</div></li>
                <li><div><b>Your days start on the first connection</b>Data begins the first time this line connects in Canada.</div></li>
              </ol>
            </section>
          </div>
        </div>
        <footer className="esim-foot">
          <div className="esim-compat-foot">
            <button type="button" className="esim-compat-note-link" onClick={() => { setCompatTab('check'); setCompatOpen(true); trackEsimEvent({ name: 'compat_opened', source: 'success' }); }}>Check compatibility</button>
            <a className="esim-help" href={order.supportUrl || 'https://canadawaleyar.com'}>Need help installing?</a>
          </div>
        </footer>
        <p className="esim-legal">Sold by Canadawale Yar. Network service is provided by the carrier partner.</p>
        {leaveOpen ? (
          <div className="esim-dialog-scrim" role="presentation" onClick={() => setLeaveOpen(false)}>
            <div className="esim-dialog" role="dialog" aria-modal="true" aria-labelledby="leave-title" onClick={(event) => event.stopPropagation()}>
              <h2 id="leave-title">Save the QR first?</h2>
              <p>You have not downloaded or copied it. You can still open this order from its link later.</p>
              <div className="esim-dialog-actions">
                <button type="button" className="esim-btn esim-btn-ghost" onClick={() => setLeaveOpen(false)}>Stay</button>
                <button type="button" className="esim-btn esim-btn-primary" onClick={onBuyAnother}>Leave</button>
              </div>
            </div>
          </div>
        ) : null}
        <CompatibilityDialog open={compatOpen} tab={compatTab} onClose={() => setCompatOpen(false)} onTab={setCompatTab} />
      </div>
    </main>
  );
};

function CheckIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
}
function PlusIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>;
}
